<template>
  <div>
    <b-modal
      id="share-proof-from-fb"
      ref="modal"
      modal-class="share-image-proof-modal"
      content-class="share-image-proof-modal__content"
      body-class="share-image-proof-modal__body"
      hide-header
      hide-footer
      centered
      no-close-on-backdrop
      @show="onShow"
      @hide="onHide"
    >
      <div class="share-image-proof-modal__panel">
        <div class="share-image-proof-modal__header">
          <h2 class="share-image-proof-modal__title">
            Share Proof
          </h2>
          <button
            type="button"
            class="share-image-proof-modal__close"
            aria-label="Close"
            :disabled="isSharing"
            @click="close()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <fieldset
          class="share-image-proof-modal__fields"
          :class="{ 'share-image-proof-modal__fields--locked': formLocked }"
          :disabled="formLocked"
          :aria-busy="isSharing ? 'true' : 'false'"
        >
          <div class="share-image-proof-modal__field">
            <label
              class="share-image-proof-modal__label"
              for="share-image-proof-layout-name"
            >Layout name</label>
            <input
              id="share-image-proof-layout-name"
              v-model="layoutName"
              type="text"
              class="share-image-proof-modal__input"
              placeholder="Layout name"
              :disabled="formLocked"
            >
          </div>

          <label class="share-image-proof-modal__check">
            <input
              v-model="notifyActivity"
              type="checkbox"
              :disabled="formLocked"
            >
            <span
              class="share-image-proof-modal__check-box"
              aria-hidden="true"
            >
              <svg
                class="share-image-proof-modal__check-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Notify me of proof activity
          </label>

          <div class="share-image-proof-modal__notify-others">
            <label class="share-image-proof-modal__check">
              <input
                v-model="notifyOthers"
                type="checkbox"
                :disabled="formLocked"
              >
              <span
                class="share-image-proof-modal__check-box"
                aria-hidden="true"
              >
                <svg
                  class="share-image-proof-modal__check-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              Notify others…
            </label>

            <div
              v-if="notifyOthers"
              class="share-image-proof-modal__notify-others-body"
            >
              <input
                v-model="emailDraft"
                type="text"
                class="share-image-proof-modal__input"
                placeholder="Add email addresses"
                :disabled="formLocked"
                @keydown="onEmailKeydown"
                @blur="commitEmailDraft"
              >

              <div
                v-if="notifyOthersEmails.length"
                class="share-image-proof-modal__chips"
              >
                <span
                  v-for="email in notifyOthersEmails"
                  :key="email"
                  class="share-image-proof-modal__chip"
                >
                  {{ email }}
                  <button
                    type="button"
                    class="share-image-proof-modal__chip-remove"
                    :aria-label="`Remove ${email}`"
                    :disabled="formLocked"
                    @click="removeEmail(email)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </span>
              </div>
            </div>
          </div>

          <label class="share-image-proof-modal__check">
            <input
              v-model="requestApproval"
              type="checkbox"
              :disabled="formLocked"
            >
            <span
              class="share-image-proof-modal__check-box"
              aria-hidden="true"
            >
              <svg
                class="share-image-proof-modal__check-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Request approval
          </label>

          <label class="share-image-proof-modal__check">
            <input
              v-model="shareRecentOnly"
              type="checkbox"
              :disabled="formLocked"
            >
            <span
              class="share-image-proof-modal__check-box"
              aria-hidden="true"
            >
              <svg
                class="share-image-proof-modal__check-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Share most recent version only
          </label>

          <label class="share-image-proof-modal__check">
            <input
              v-model="dailyReminder"
              type="checkbox"
              :disabled="formLocked"
            >
            <span
              class="share-image-proof-modal__check-box"
              aria-hidden="true"
            >
              <svg
                class="share-image-proof-modal__check-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Send daily reminders for unresolved comments
          </label>
        </fieldset>

        <div
          v-if="proofUrl"
          class="share-image-proof-modal__link"
        >
          <label class="share-image-proof-modal__label">Share a link</label>
          <div class="share-image-proof-modal__link-row">
            <input
              ref="copyTarget"
              class="share-image-proof-modal__link-input"
              :value="proofUrl"
              readonly
              @click="copyLink"
            >
            <button
              type="button"
              class="share-image-proof-modal__icon-btn"
              title="Copy link"
              aria-label="Copy link"
              @click="copyLink"
            >
              <i class="fa fa-clipboard" />
            </button>
            <a
              class="share-image-proof-modal__icon-btn"
              :href="proofUrl"
              target="_blank"
              rel="noopener noreferrer"
              title="Open link"
              aria-label="Open link"
            >
              <i class="fa fa-external-link-alt" />
            </a>
          </div>
          <p
            v-if="copied"
            class="share-image-proof-modal__copied"
          >
            Link copied to clipboard!
          </p>
        </div>

        <div class="share-image-proof-modal__footer">
          <button
            type="button"
            class="share-image-proof-modal__btn-cancel"
            :disabled="isSharing"
            @click="close()"
          >
            {{ proofUrl ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-if="!proofUrl"
            type="button"
            class="share-image-proof-modal__btn-share"
            :disabled="!shareAvailable || isSharing"
            @click="handleOkBtn"
          >
            <b-spinner
              v-if="isSharing"
              small
            />
            Share Proof
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import mixin from './mixin.js';
import Layout from '@frontend/services/api/layout';
import ShareProofService from '@frontend/services/api/share-proof';
import { IMPORT_AND_CREATE_STRATEGY } from '@frontend/group/modules/import-template-from-psd/import-from-psd-new-template';
import { createNamespacedHelpers } from 'vuex';
import { SIMPLE_IMAGE_TYPES } from '@frontend/components/modules/file-browser/constants';
import { isProcessingError } from '@frontend/components/modules/file-browser/helpers.js';

const { mapGetters } = createNamespacedHelpers('fileBrowser');

export default {
  name: 'ShareProofModal',
  mixins: [mixin],
  data: () => ({
    layoutName: '',
    notifyActivity: true,
    notifyOthers: true,
    notifyOthersEmails: [],
    emailDraft: '',
    requestApproval: true,
    shareRecentOnly: false,
    dailyReminder: false,
    isSharing: false,
    proofUrl: null,
    copied: null,
    echoChannel: null,
  }),
  computed: {
    ...mapGetters(['selectedItems', 'breadcrumbs']),
    formLocked() {
      return this.isSharing || Boolean(this.proofUrl);
    },
    shareAvailable() {
      return this.layoutName.length > 0;
    },
    currentFolderName() {
      return this.breadcrumbs[this.breadcrumbs.length - 1]?.name || '';
    },
    isFolder() {
      return this.selectedItems.length === 1 && this.selectedItems[0].type === 'folder';
    },
  },
  methods: {
    init() {
      this.proofUrl = null;
      this.copied = null;
      this.isSharing = false;
      this.notifyActivity = true;
      this.notifyOthers = true;
      this.notifyOthersEmails = [];
      this.emailDraft = '';
      this.requestApproval = true;
      this.shareRecentOnly = false;
      this.dailyReminder = false;

      if (this.isFolder) {
        this.layoutName = this.selectedItems[0].name;
      } else {
        this.layoutName = this.currentFolderName;
      }

      this.$refs.modal.show();
    },
    close(silent = false) {
      if (this.isSharing) {
        return;
      }
      this._silentClose = silent;
      this.leaveEchoChannel();
      this.$refs.modal?.hide();
    },
    onShow() {
      document.body.classList.add('share-image-proof-modal-open');
    },
    onHide() {
      document.body.classList.remove('share-image-proof-modal-open');
      this.leaveEchoChannel();
      this.closeHandler();
    },
    getUserId() {
      return $('meta[name="userId"]').attr('content');
    },
    leaveEchoChannel() {
      if (this.echoChannel) {
        window.Echo.leaveChannel(`user.${this.getUserId()}`);
        this.echoChannel = null;
      }
    },
    listenForProofReady() {
      this.echoChannel = window.Echo.channel(`user.${this.getUserId()}`)
        .listen('.proof-images-generation-finished', () => {
          this.leaveEchoChannel();
          window.open(this.proofUrl, '_blank');
        });
    },
    isValidEmail(value) {
      const trimmed = String(value || '').trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    },
    onEmailKeydown(event) {
      if (this.formLocked) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (String(this.emailDraft || '').trim()) {
          event.preventDefault();
          this.commitEmailDraft();
        }
        return;
      }

      if ([',', ';', ' '].includes(event.key)) {
        event.preventDefault();
        this.commitEmailDraft();
      }
    },
    commitEmailDraft() {
      if (this.formLocked) {
        return;
      }
      const parts = String(this.emailDraft || '')
        .split(/[,;\s]+/)
        .map((part) => part.trim())
        .filter(Boolean);

      if (!parts.length) {
        this.emailDraft = '';
        return;
      }

      const next = [...this.notifyOthersEmails];
      parts.forEach((email) => {
        if (!this.isValidEmail(email)) return;
        const exists = next.some(
          (item) => String(item).trim().toLowerCase() === email.toLowerCase(),
        );
        if (!exists) next.push(email);
      });

      this.notifyOthersEmails = next;
      this.emailDraft = '';
    },
    removeEmail(email) {
      if (this.formLocked) {
        return;
      }
      const key = String(email).trim().toLowerCase();
      this.notifyOthersEmails = this.notifyOthersEmails.filter(
        (item) => String(item).trim().toLowerCase() !== key,
      );
    },
    async handleOkBtn() {
      if (this.isSharing || !this.shareAvailable) {
        return;
      }
      this.commitEmailDraft();
      this.isSharing = true;

      let selectedItems = this.selectedItems;

      if (this.isFolder) {
        selectedItems = await this.getFolderContent(this.selectedItems[0].id);

        const brokenItems = selectedItems.filter(isProcessingError);

        if (brokenItems.length) {
          const label = brokenItems.length === 1 ? 'This file is broken' : 'These files are broken';
          const fileNames = brokenItems.map(i => `•${i.name}`).join('<br>');

          toastr.error(`Cannot generate proof. ${label}:<br><br>${fileNames}`, null, {
            escapeHtml: false,
          });

          this.isSharing = false;
          this.close();
          return;
        }
      }

      try {
        const productImages = selectedItems.map(i => i.path);
        const backgroundThemeImages = selectedItems.map(i => i.original_path);

        const { data } = await Layout.createLayoutAndTemplateFromImage(0, {
          name: this.layoutName,
          strategy: IMPORT_AND_CREATE_STRATEGY,
          productImages,
          backgroundThemeImages,
        });

        const { data: proofData } = await ShareProofService.generateProofPSD3(data.layout_id);
        const linkHash = proofData.newProof?.link;

        const { data: linksData } = await ShareProofService.getSharedProofLinks(data.layout_id);
        const latestProof = linksData.data?.[0];

        if (linkHash) {
          await ShareProofService.updateSettings(data.layout_id, linkHash, {
            id: this.getUserId(),
            notify: String(this.notifyActivity),
            approve: String(this.requestApproval),
            hide_versions_dropdown: String(this.shareRecentOnly),
            daily_reminder: String(this.dailyReminder),
            email_other: this.notifyOthers && this.notifyOthersEmails.length
              ? this.notifyOthersEmails.join(',')
              : null,
          });
        }

        this.proofUrl = latestProof?.link || proofData.newProof?.url;
        this.listenForProofReady();
      } catch (e) {
        toastr.error('Something went wrong while sharing proof');
      } finally {
        this.isSharing = false;
      }
    },
    async copyLink() {
      const input = this.$refs.copyTarget;
      if (input?.select) input.select();
      try {
        await navigator.clipboard.writeText(this.proofUrl);
        this.copied = true;
        setTimeout(() => { this.copied = null; }, 1400);
      } catch (error) {
        if (input) {
          input.select();
          document.execCommand('copy');
          this.copied = true;
          setTimeout(() => { this.copied = null; }, 1400);
        }
      }
    },
    async getFolderContent(id) {
      const { data } = await axios.get('/file/folder_content', {
        params: {
          folderId: id,
          pageNumber: 1,
          pageSize: 50,
        },
      });

      return data.items.filter(i => SIMPLE_IMAGE_TYPES.includes(i.type));
    },
  },
};
</script>
